import { TabStyles } from '@tableau/widgets';
import * as React from 'react';

export module CommonStyles {

  export module Colors {

    export const White: string = 'rgb(255,255,255)';
    export const Black: string = 'rgb(0,0,0)';

    export const Grey40: string = 'rgb(40,40,40)';
    export const Grey51: string = 'rgb(51,51,51)';
    export const Grey68: string = 'rgb(68,68,68)';
    export const Grey89: string = 'rgb(89,89,89)';
    export const Grey102: string = 'rgb(102,102,102)';
    export const Grey136: string = 'rgb(136,136,136)';
    export const Grey153: string = 'rgb(153,153,153)';
    export const Grey178: string = 'rgb(178,178,178)';
    export const Grey203: string = 'rgb(203,203,203)';
    export const Grey218: string = 'rgb(218,218,218)';
    export const Grey225: string = 'rgb(225,225,225)';
    export const Grey230: string = 'rgb(230,230,230)';
    export const Grey235: string = 'rgb(235,235,235)';
    export const Grey245: string = 'rgb(245,245,245)';
    export const Grey250: string = 'rgb(250,250,250)';

    export const BlueHighlight: string = 'rgb(212,225,237)';
    export const DiscreteBlue: string = 'rgb(73,150,178)';
    export const HyperlinkBlue: string = 'rgb(42,121,175)';

    export const BorderColor: string = Colors.Grey178;
    export const GreyTextColor: string = Colors.Grey153;
    export const NormalTextColor: string = Colors.Grey102;
    export const DisabledTextColor: string = Colors.Grey218;

    export const ActionOrange: string = 'rgb(235,143,80)';
    export const ActionOrangeDark: string = 'rgb(209,107,54)';

    export const TextButtonHoverFillColor: string = Colors.Grey230;

    export const DimensionBlueDark: string = TabStyles.Colors.DiscreteDark;

    export const DividerLineColor: string = Colors.Grey230;
  }

  export module Sizes {

    export const MinSupportedAppWidth: number = 1000;

    export const TextBoxPadding: number = 4;
    export const TextBoxBorderSize: number = 1;

    export const LineWidth: number = 1;
    export const IconBorderWidth: number = 2;
    export const SmallSpace: number = 3;
    export const DefaultSpace: number = 6;
    export const MediumSpace: number = 12;
    export const LineHeight: number = 16;
    export const LargeSpace: number = 18;
    export const RowHeight: number = 23;
    export const XLargeSpace: number = 35;
    export const DropdownItemHeight: number = 30;

    export const SmallFontSize: number = 11;
    export const NormalFontSize: number = 12;
    export const LargeFontSize: number = 13;
    export const HeaderFontSize: number = 13;

    export const IconHoverExpansion: number = 12;
    export const ExtraSmallIconSize: number = 14;
    export const SmallIconSize: number = 17;
    export const SmallIconBackgroundSize: string = SmallIconSize + 'px ' + SmallIconSize + 'px';
    export const NormalIconSize: number = 19;
    export const NormalIconBackgroundSize: string = NormalIconSize + 'px ' + NormalIconSize + 'px';

    // should match $breadcrumbsInnerHeight in place.styl
    export const BreadcrumbsHeight: number = 37;

    export const DataGridHeaderHeight: number = 37;
    // should match ListLayout.DefaultRowHeight
    export const DataGridRowHeight: number = 38;

    export const CompactRowHeight: number = 27;

    export const DataGridWithPictureIconsHeaderHeight = 44;
    export const DataGridWithPictureIconsRowHeight = 45;

    // Should match $actionBarHeight in contentSubplace.styl
    export let ActionBarHeight = 37;

    // Dropdown menus appear detached below their buttons by this many pixels
    export const DropdownOffset: number = 3;

    export const CellHeight: number = DataGridRowHeight - LineWidth;
    export const DataGridWithPictureIconsCellHeight: number = DataGridWithPictureIconsRowHeight - LineWidth;

    export const ColMinWidth: number = 150;
    export const ColMaxWidth: number = 500;
  }

  export module Fonts {
    // BodyFont is normally inherited from the base styles for the page. This style should only be
    // used to override behavior back if some other CSS rule changes it. This shouldn't be commonly used
    export const BodyFont: string = 'BentonSans-Book, Arial, Helvetica, sans-serif';

    export const LightFont: string = 'BentonSans-Light, Arial, Helvetica, sans-serif';
    export const HeaderFont: string = 'BentonSans-Book, Arial, Helvetica, sans-serif';
    export const HeavyFont: string = 'BentonSans-Medium, Arial, Helvetica, sans-serif';
    export const SemiDemi: string = 'BentonSans-SemiDemi, Arial, Helvetica, sans-serif';
  }

  export module Icons {
    export const Datasource: string = 'data_source_content_type_small.svg';
    export const Project: string = 'project_content_type_small.svg';
    export const Workbook: string = 'workbook_content_type_small.svg';
    export const View: string = 'view_content_type_small.svg';
    export const Flow: string = 'flow_content_type_small.svg';
  }

  export module Helpers {

    export function Edges(distanceToEdge: number): React.CSSProperties {
      return {
        position: 'absolute',
        left: distanceToEdge,
        right: distanceToEdge,
        top: distanceToEdge,
        bottom: distanceToEdge
      };
    }
  }

  export module Layout {
    export const FlexBlockLeftAndCenter: React.CSSProperties = {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center'
    };

    export const FlexBlockVerticalCenter: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center'
    };

    export const FlexRight: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end'
    };

    export const TextEllipsis: React.CSSProperties = {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    };

    export const TextAlignCenter: React.CSSProperties = {
      textAlign: 'center'
    };

    export const TextAlignRight: React.CSSProperties = {
      textAlign: 'right'
    };
  }

  /** Keep these in sync with those in shadow.styl */
  export module Shadows {
    export const MenuShadow: string = '0 7px 15px rgba(0,0,0,0.2)';
    export const PopoverShadow: string = '0 7px 15px rgba(0,0,0,0.2)';
  }

  export module Opacity {
    export const DisabledOpacity = 0.4;
  }

  export const Box: React.CSSProperties = {
    display: 'inline-block',
    verticalAlign: 'top'
  };
}
